import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, TextField, Typography, Box } from '@mui/material';
import { Tag } from '../../../types/Tag';
import { Nullable } from '../../../types/utils';
import { Preference, PREFERENCES_VALID_KEYS } from '../../../types/Preference';
import { useGetAllPreferences } from '../../../services/react-query/preferences';
import { headerTypographyProps } from '../../../styles/sxStyling';
import PreferencePicker from '../../preferences/picker/PreferencePicker';
import { uuidFinder } from '../../../utility/inputUtils';

interface IAddTagModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onAdd: (tag: Tag) => void;
  tag: Tag | null;
}

type ModalState = Nullable<Omit<Tag, 'uuid'>>;

const BLANK_STATE: ModalState = {
  preference_uuid: null,
  preference_value: null,
  preference_color: null,
  text: '',
};

export default function AddTagModal({ tag, isOpen, onCancel, onAdd }: IAddTagModalProps) {
  const [modalState, setModalState] = useState<ModalState>(BLANK_STATE);

  const { data: preferenceData, isLoading: isGetAllPreferencesLoading } = useGetAllPreferences({
    params: { filters: { keys: [PREFERENCES_VALID_KEYS.TAG] } },
  });

  const preferences = preferenceData || [];

  useEffect(() => {
    if (tag) {
      setModalState({ ...tag });
    } else {
      setModalState(BLANK_STATE);
    }
  }, [isOpen, tag]);

  const handleSubmit = () => {
    if (!modalState.preference_uuid) return;
    onAdd({
      uuid: tag?.uuid || crypto.randomUUID(),
      ...(modalState as Omit<Tag, 'uuid'>),
    });
    onCancel();
  };

  const selectedPref: Preference | null = (uuidFinder(preferences, modalState.preference_uuid || '') as Preference | null) ?? null;

  return (
    <Dialog open={isOpen} onClose={onCancel} fullWidth>
      <Typography {...headerTypographyProps} sx={{ m: 2 }}>
        Please enter tag data
      </Typography>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <PreferencePicker
            label="Tag Preference"
            options={preferences}
            loading={isGetAllPreferencesLoading}
            value={selectedPref}
            onValueChange={(val) => {
              const pref = (val as Preference | null) ?? null;
              setModalState((prev) => ({
                ...prev,
                preference_uuid: pref?.uuid ?? null,
                preference_value: pref?.value ?? null,
                preference_color: pref?.color ?? null,
              }));
            }}
            disableClearable={false}
          />

          <TextField
            label="Tag Text"
            size="small"
            value={modalState.text ?? ''}
            onChange={(e) => setModalState({ ...modalState, text: e.target.value })}
            fullWidth
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" disabled={!modalState.preference_uuid} onClick={handleSubmit}>
          {tag ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
